import React from 'react';
import styled from 'styled-components';
import { Trans } from '@lingui/macro';
import { IconList, type IconListItem } from './IconList';
import {
  AccountCardGlyph,
  AccountFundingGlyph,
  ConfirmationGlyph,
  DelegateCreationGlyph,
  FileValidationGlyph,
} from './glyphs';
import { tok, UN_BLUE } from './styles';

/**
 * Left column (PRD §2 phase 1 / §10): the small caption, "What happens when
 * uploading a file" heading, the intro paragraphs and the "Step-by-Step
 * Process" icon list. No bordered card — the live page renders this as plain
 * content. The header-right controls live in the `Actions` column.
 */
const Caption = styled.div`
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
  color: ${({ theme }) => tok(theme, 'general_font_tertiary', '#696969')};
`;

const H2 = styled.h2`
  margin: 0 0 24px;
  font-size: 24px;
  line-height: 32px;
  font-weight: 700;
  color: ${({ theme }) => tok(theme, 'general_font_primary', '#1E1E1E')};
`;

const Para = styled.p`
  margin: 0 0 16px;
  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
  color: ${({ theme }) => tok(theme, 'general_font_primary', '#1E1E1E')};
`;

const SectionTitle = styled.div`
  margin: 8px 0 20px;
  font-size: 20px;
  line-height: 28px;
  font-weight: 400;
  color: ${({ theme }) => tok(theme, 'general_font_tertiary', '#696969')};
`;

export const Instructions: React.FC<{ companyCif: string }> = ({ companyCif }) => {
  const items: IconListItem[] = [
    {
      icon: <FileValidationGlyph size={20} color={UN_BLUE} />,
      title: <Trans>File validation</Trans>,
      description: (
        <Trans>
          The system will validate the data line by line. Any invalid rows will be extracted into a
          separate file, which you can download to review and correct the errors.
        </Trans>
      ),
    },
    {
      icon: <DelegateCreationGlyph size={20} color={UN_BLUE} />,
      title: <Trans>Delegate creation</Trans>,
      description: (
        <Trans>
          Each delegate listed in the file will be created using the provided data. They will
          receive an email invitation to access the portal and set up their login credentials. If a
          delegate already exists for this conference, the system will skip this step and move
          directly to funding their account.
        </Trans>
      ),
    },
    {
      icon: <AccountCardGlyph size={20} color={UN_BLUE} />,
      title: <Trans>Account and card creation</Trans>,
      description: (
        <Trans>
          A bank account on the required currency and a corresponding debit card will be created for
          each delegate, ready for immediate use.
        </Trans>
      ),
    },
    {
      icon: <AccountFundingGlyph size={20} color={UN_BLUE} />,
      title: <Trans>Account funding</Trans>,
      description: (
        <Trans>
          If the option 'Execute payments' has been selected, each delegate's account will be funded
          with the amount specified in the uploaded file. The source of payment will be the account
          of this conference in the applicable currency labelled Main and the payment reference will
          use the conference identifier — in this case, {companyCif}. Delegates will be
          notified by email of the payment and its amount.
        </Trans>
      ),
    },
    {
      icon: <ConfirmationGlyph size={20} color={UN_BLUE} />,
      title: <Trans>Confirmation</Trans>,
      description: (
        <Trans>
          Once the process is complete, you will receive a confirmation email summarising the
          result. If you selected the option to execute payments, the email will include the total
          amount transferred to delegates. It will also contain a detailed error report for any
          records that could not be processed successfully.
        </Trans>
      ),
    },
  ];

  return (
    <div>
      <Caption>
        <Trans>Step by step instructions</Trans>
      </Caption>
      <H2>
        <Trans>What happens when uploading a file</Trans>
      </H2>
      <Para>
        <Trans>
          Before uploading your file, please ensure you've reviewed the necessary requirements. The
          easiest way to get started is by downloading the example file using the button next to
          this paragraph.
        </Trans>
      </Para>
      <Para>
        <Trans>
          If you want payments to be executed for the delegates listed in your file, make sure to
          check the "Execute payments" box before uploading. If this option is not selected, the
          Payment Amount column will be ignored and no payments will be processed, even if amounts
          are included in the file.
        </Trans>
      </Para>
      <Para>
        <Trans>
          Once your file is ready and structured according to the example provided, prepare your data
          accordingly and upload the CSV file by clicking the "Upload file for delegate creation"
          button in the top right corner of this page. The rest of the process will happen
          automatically, as outlined below:
        </Trans>
      </Para>
      <SectionTitle>
        <Trans>Step-by-Step Process</Trans>
      </SectionTitle>
      <IconList items={items} />
    </div>
  );
};
